import { SyntheticEvent, FC } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Control } from 'react-hook-form';
import { ReleaseTaskFormField, Condition } from 'types/releaseTask';
import { CustomerList } from 'page/ReleasePage/TaskPage/containers/TaskRegistrationModal/CustomerList';
import { SelectTargetGroupInput } from 'page/ReleasePage/Forms';
import { EditHookReturnType } from '../../../types';
import {
  PlaneTextInput,
  SelectTextInput,
  ConditionForm,
  TaskEndDateTimePicker,
  TaskStartDateTimePicker,
} from '../../../../Forms';

type Props = EditHookReturnType['form'] & {
  mode: '登録' | '更新';
  handleConfirm: () => void;
  onOpenModalDelete: () => void;
  control: Control<ReleaseTaskFormField>;
  releaseEnvironments: { id: string; name: string }[];
  targetGroupList: { id: string; name: string }[];
  handleChangeCondition: (condition: Condition) => void;
  shouldShowCustomerList: boolean;
  handleChangeSelectTargetGroupInput: (
    e: SyntheticEvent<HTMLSelectElement>,
  ) => void;
};

export const TaskRegistrationInputModalView: FC<Props> = ({
  mode,
  register,
  getValues,
  trigger,
  setValue,
  formState,
  handleConfirm,
  onOpenModalDelete,
  control,
  releaseEnvironments,
  targetGroupList,
  handleChangeCondition,
  shouldShowCustomerList,
  handleChangeSelectTargetGroupInput,
}) => (
  <ModalContent maxHeight="90vh">
    <ModalHeader p="40px">タスクの{mode}</ModalHeader>
    <ModalCloseButton />
    <ModalBody overflowY="auto" px="40px">
      <Stack spacing="6" direction="column">
        <FormControl isRequired isInvalid={!!formState.errors.taskName}>
          <FormLabel>タスク名</FormLabel>
          <PlaneTextInput name="taskName" register={register} />
          <FormErrorMessage>
            {formState.errors.taskName?.message}
          </FormErrorMessage>
        </FormControl>

        <Stack pointerEvents={(mode === '登録' && 'auto') || 'none'}>
          <FormLabel>環境名</FormLabel>
          <FormControl bg={(mode === '登録' && 'auto') || '#ccc'}>
            <SelectTextInput
              list={releaseEnvironments}
              name="version"
              register={register}
            />
            <FormErrorMessage>
              {formState.errors.version?.message}
            </FormErrorMessage>
          </FormControl>
        </Stack>
        <Flex flexDirection="row" gap={5} alignItems="start">
          <Box>
            <FormControl isRequired isInvalid={!!formState.errors.startsAt}>
              <FormLabel>タスク開始日</FormLabel>
              <TaskStartDateTimePicker
                name="startsAt"
                control={control}
                inputProps={{ width: '328px' }}
                endDateTimeProps={{
                  name: 'endsAt',
                  getValues,
                  setValue,
                  trigger,
                }}
              />
              <FormErrorMessage>
                {formState.errors.startsAt?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
          <Box w="20px" pt="40px">
            <Text as="span">〜</Text>
          </Box>
          <Box>
            <FormControl isRequired isInvalid={!!formState.errors.endsAt}>
              <FormLabel>タスク終了日</FormLabel>
              <TaskEndDateTimePicker
                name="endsAt"
                control={control}
                inputProps={{ width: '328px' }}
                startDateTimeProps={{ name: 'startsAt', getValues }}
              />
              <FormErrorMessage>
                {formState.errors.endsAt?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
        </Flex>
        <FormControl isRequired isInvalid={!!formState.errors.group}>
          <FormLabel>ターゲットグループ</FormLabel>
          <SelectTargetGroupInput
            list={targetGroupList}
            name="group"
            register={register}
            onChange={handleChangeSelectTargetGroupInput}
          />
          <FormErrorMessage>{formState.errors.group?.message}</FormErrorMessage>
        </FormControl>
        {/* 配信対象顧客 */}
        <Box mt="40px">
          <ConditionForm
            control={control}
            handleChangeCondition={handleChangeCondition}
          />
        </Box>
        {/* 顧客一覧 */}
        <Box mt="40px" mb="40px">
          {shouldShowCustomerList && <CustomerList />}
        </Box>
      </Stack>
    </ModalBody>
    <ModalFooter justifyContent="center" p="40px">
      <Stack alignItems="center" spacing="24px">
        <Button
          w="140px"
          colorScheme="blackAlpha"
          onClick={handleConfirm}
          isDisabled={!formState.isValid}
        >
          確認
        </Button>
        {mode === '更新' && (
          <Button
            variant="link"
            colorScheme="blackAlpha"
            onClick={onOpenModalDelete}
          >
            このタスクを削除
          </Button>
        )}
      </Stack>
    </ModalFooter>
  </ModalContent>
);
